import { assertLinkRequestActive } from '#~/api/agent/link-request-expiry.js';
import type { LinkRequestLifetime } from '#~/api/agent/link-request-expiry.js';
import { LinkRequestLifetimeNotice, useLinkRequestLifetime } from './link-request-lifetime.jsx';
import { useRenderedBootHumanWait } from '#~/routes/boot-overlay/boot-budget-view.js';
import * as Wizard from '#~/agent/components/wizard/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import type { LinkRequest } from '#~/api/agent/index.js';
import {
  expectAgentInRoom,
  fetchAgentsForRoom,
  fetchRequestLink,
  linkAgentWithAuth
} from '#~/api/agent/index.js';
import { ApiError } from '#~/api/types.js';
import { useAuth0 } from '#~/auth/index.js';
import { Credentials as _Credentials } from '#~/common/editor/index.js';
import { NotFoundError } from '#~/error/index.js';
import { Paths } from '#~/routes/index.js';
import { StudioCreatedNotice } from './studio-created-notice.jsx';
import { Anchor } from '@studio/design-system/button';
import { Loader } from '@studio/design-system/loader';
import T from '@studio/design-system/themes';
import { unwrap } from '@studio/js-utils/async';
import { normalizeId } from '@studio/models';
import type { Agent } from '@studio/models/agent';
import type { Credentials as M_Credentials } from '@studio/models/auth';
import type { Room } from '@studio/models/room';
import { toAccessor } from '@studio/react-utils';
import { useAsyncEffect, useLatestFn } from '@studio/react-utils/hooks';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { queryKeys } from '#~/react-query/index.js';
import { Hardware } from './hardware.jsx';
import { awaitLinkedAgentInRoom } from './link-settle.js';
import { reportClaimLinked, resolveClaimLinkResponse } from './link-response.js';
import { Problem } from './problem.jsx';
import { Sel, STag } from '#~/stag/tag.js';
// import { error } from '@techstark/opencv-js';

interface DeviceLoginLockoutStatus {
  $type: 'api.v1.DeviceLoginLockoutStatus';
  locked: boolean;
  loginAttemptsRemaining: number;
  unlockTimeSeconds: number;
}

/** `<Auth>` component. */
export const Auth = (): ReactElement => {
  const params = useParams();
  const location = useLocation();
  // Set by the create-studio step (ENG-4209) when it just provisioned — or was
  // deduped onto — the Studio being linked. Undefined when the user picked an
  // existing Studio instead, in which case there is no create outcome to report.
  const createdAlreadyExisted = location.state?.alreadyExisted as boolean | undefined;
  const agentId = normalizeId<Agent['id']>(params.agentId);
  const requestId = normalizeId<LinkRequest['id']>(params.requestId);
  const roomId = normalizeId<Room['id']>(params.roomId);

  if (!agentId || !requestId) {
    throw new NotFoundError();
  }

  const [authCredentials, setAuthCredentials] = useState<M_Credentials>();
  const [agent, setAgent] = useState<
    Omit<Agent, 'lastShutDownAt' | 'roomId' | 'status'> & LinkRequestLifetime
  >();
  const [isBusy, setBusy] = useState(false);

  const [agentError, setAgentError] = useState<ApiError>();
  const lifetime = useLinkRequestLifetime(agent?.linkExpiresAt, agentError);
  const [authError, setAuthError] = useState<ApiError>();
  const [lockoutStatus, setLockoutStatus] = useState<DeviceLoginLockoutStatus>();
  const [remainingTime, setRemainingTime] = useState<number>();

  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const handleLink = useLatestFn(async () => {
    try {
      const token = await getAccessTokenSilently();
      assertLinkRequestActive(agent?.linkExpiresAt);
      const response = await linkAgentWithAuth(
        agentId,
        requestId,
        roomId!,
        token,
        authCredentials?.username,
        authCredentials?.password
      );

      if (!response) {
        throw new Error('Link response is empty.');
      }

      if (response.error?.code === 412) {
        setAgentError(new ApiError(response.error.message, { code: 412 }));
        return;
      }

      if (response.deviceLoginLockoutStatus) {
        setLockoutStatus(response.deviceLoginLockoutStatus);
      }

      // The rejected-credential telemetry lives with the classification so the
      // e2e receipt exercises the same code this screen runs.
      const outcome = resolveClaimLinkResponse({
        response,
        agentId,
        ...(roomId !== undefined ? { studioId: roomId } : {}),
        ...(typeof agent?.embedded === 'boolean' ? { embedded: agent.embedded } : {}),
        track
      });
      switch (outcome.kind) {
        case 'locked':
          setRemainingTime(outcome.unlockTimeSeconds);
          return; // Don't proceed with navigation if device is locked
        case 'auth-error':
          setAuthError(new ApiError(outcome.message));
          return;
        case 'agent-error':
          setAgentError(new ApiError(outcome.message));
          return;
        case 'linked':
          break;
      }

      // embedded link settle: a single force refresh here raced registry
      // read-after-write propagation — when it lost, it re-cached the stale
      // PRE-link agent set for ROOM_CACHE_TTL (60s) and nothing downstream
      // re-enumerates (useRoomAgentStatus: retry:false, no refetchInterval), so
      // /room/:id/boot dead-spotted on "Loading your studio…" until a manual
      // refresh (QA: relink-to-empty-studio + offline/online-bridge link flows).
      // Poll until the just-linked agent actually appears (each probe bypasses
      // the room cache), mirroring Electron's awaitAutoLinkSettle.
      // Per rule: reference-rules-in-comments + think-in-systems (ai.knowledge/global).
      // If the poll below gives up, the room still waits for this camera
      // instead of loading without it (api/agent/registry.ts, expectAgentInRoom).
      expectAgentInRoom(roomId!, agentId);
      const settled = await awaitLinkedAgentInRoom({
        agentId,
        fetchAgents: async () => fetchAgentsForRoom(roomId!, await getAccessTokenSilently(), true)
      });

      if (!settled) {
        // Navigate anyway — the boot route's stall watchdog is the terminal
        // backstop; stranding the user in the wizard would be strictly worse.
        console.warn(
          'Studio: link: agent not in room enumeration after settle poll; navigating anyway',
          { agentId, roomId }
        );
      }

      // The room's agent-status query may still hold the pre-link snapshot
      // (staleTime 10s — e.g. the Linker screen of the same room enumerated
      // moments ago on a relink). Invalidate so the boot route re-runs it
      // against the fresh room cache the settle poll just populated.
      await queryClient.invalidateQueries({ queryKey: queryKeys.agentStatus(roomId!) });

      // Success-side telemetry and the claim-without-shadow clock live with the
      // reply classifier so the e2e receipt exercises the same code.
      reportClaimLinked({
        agentId,
        ...(roomId !== undefined ? { studioId: roomId } : {}),
        ...(typeof agent?.embedded === 'boolean' ? { embedded: agent.embedded } : {}),
        track
      });

      // Navigate to the room, booting the agent up!
      navigate(Paths.room.boot.resolve(roomId!));
    } catch (error_) {
      const errorMessage = error_ instanceof Error ? error_.message : String(error_);
      setAgentError(error_ instanceof ApiError ? error_ : new ApiError(errorMessage));
    } finally {
      setBusy(false);
    }
  });

  const handleAgentAuth = useLatestFn(async () => {
    if (lifetime.expired || !authCredentials?.username || !authCredentials?.password) {
      return;
    }

    setBusy(true);

    try {
      await handleLink();
    } catch (error) {
      setAuthError({ message: error instanceof Error ? error.message : String(error) } as ApiError);
    } finally {
      setBusy(false);
    }
  });

  useEffect(() => {
    if (!lockoutStatus?.locked || remainingTime === undefined) return;

    if (remainingTime <= 0) {
      setLockoutStatus(undefined);
      return;
    }

    const timer = setInterval(() => {
      setRemainingTime((prev) => {
        const nextValue = (prev ?? 0) - 1;
        if (nextValue <= 0) {
          clearInterval(timer);
          setLockoutStatus(undefined);
        }
        return nextValue;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [lockoutStatus?.locked, remainingTime]);

  useAsyncEffect(
    function* generator(signal: AbortSignal) {
      try {
        const token = yield* unwrap(getAccessTokenSilently());
        const response = yield* unwrap(fetchRequestLink(requestId, token));
        setAgent({ ...response, id: agentId });
      } catch (error_) {
        if (!signal.aborted) {
          setAgentError(error_ as ApiError);
        }
      }
    },
    [requestId]
  );

  useRenderedBootHumanWait(!isBusy && (!!agent || !!agentError));

  // Let an already submitted claim settle; expiry prevents new actions.
  if (lifetime.expired && !isBusy) return <LinkRequestLifetimeNotice {...lifetime} />;

  if (!agent && !agentError) {
    return <Loader />;
  }

  const authMessage = `This is the admin login you use to access your device. Typically, this is the credentials you enter when accessing the web interface for your Device.`;

  return (
    <Wizard.Root currentStep={3} totalSteps={3}>
      <LinkRequestLifetimeNotice {...lifetime} />
      {!!agent && (
        <>
          <Hardware agent={agent} />
          {/* Report how the create resolved on the screen the user actually
              stops on: "linked to your existing Studio" vs "new Studio created"
              (ENG-4209). */}
          {createdAlreadyExisted !== undefined && (
            <StudioCreatedNotice alreadyExisted={createdAlreadyExisted} />
          )}
          <Wizard.Separator />
        </>
      )}

      {!!agentError && (
        <Problem
          agent={{ ...agent, id: agentId }}
          error={Object.assign({}, agentError, {
            title: 'Linking Error',
            message: agentError.message
          })}
        >
          <Anchor
            className='text-primary'
            onClick={(): void => {
              navigate(Paths.agent.link.confirm.resolve(agentId, requestId));
            }}
          >
            Try again
          </Anchor>
        </Problem>
      )}

      {!!agent && !agentError && (
        <>
          <Wizard.Verbiage
            message={authMessage}
            title='Enter Device Credentials'
            className='w-full font-medium'
          />

          <div className='w-[93%]'>
            <_Credentials
              data-theme={T.getTheme('light')}
              labelClassName='text-base-content'
              inputClassName={`border ${authError ? 'border-error' : 'border-base-300'}`}
              inputSize='lg'
              credentials={toAccessor(authCredentials, setAuthCredentials)}
              className={lockoutStatus?.locked ? 'pointer-events-none opacity-50' : ''}
            />
            {(authError || lockoutStatus?.locked) && (
              <div className='mt-10 flex items-center gap-8 rounded-8 bg-error/10 px-12 py-8 text-error'>
                <span className='text-sm'>
                  {lockoutStatus?.locked
                    ? `The device is now locked. Please wait ${remainingTime} seconds before trying again.`
                    : authError
                      ? 'Invalid username or password, please try again.'
                      : null}
                </span>
              </div>
            )}
          </div>

          <Wizard.ButtonBox>
            <Wizard.Button
              busy={isBusy}
              disabled={
                isBusy ||
                !authCredentials?.username ||
                !authCredentials?.password ||
                lockoutStatus?.locked
              }
              onClick={handleAgentAuth}
              // Hardware tests submit the claim through this tag.
              // Per rule: playwright-stag-selectors-only (ai.knowledge/platform).
              {...STag(Sel.AgentLinkClaim, 'submit')}
            >
              {isBusy
                ? `Validating ${agent.embedded ? 'Camera' : 'Agent'}...`
                : `Confirm & Link ${agent.embedded ? 'Camera' : 'Agent'}`}
            </Wizard.Button>
          </Wizard.ButtonBox>
        </>
      )}
    </Wizard.Root>
  );
};
